import { useRef, KeyboardEvent, ClipboardEvent, ChangeEvent } from 'react';
import { VerificationCodeInputContainer, VerificationCodeInput } from './styled';

export interface AuthVerificationCodeInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  onComplete?: (code: string) => void;
}

/**
 * 6-digit verification code input component with auto-focus and paste support.
 */
export function AuthVerificationCodeInput({
  length = 6,
  value,
  onChange,
  disabled = false,
  onComplete,
}: AuthVerificationCodeInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, digit: string) => {
    // Only allow single digits
    if (digit.length > 1) {
      return;
    }

    // Only allow numbers
    if (digit && !/^\d$/.test(digit)) {
      return;
    }

    // Update the value
    const newValue = value.split('');
    newValue[index] = digit;
    const updatedValue = newValue.join('');
    onChange(updatedValue);

    // Auto-focus next input if digit was entered
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Call onComplete if we just filled the last digit
    if (digit && index === length - 1 && updatedValue.length === length && onComplete) {
      onComplete(updatedValue);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        // If current input is empty, focus previous input
        inputRefs.current[index - 1]?.focus();
      } else {
        // Clear current input
        handleChange(index, '');
      }
    }
    // Handle arrow keys
    else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();

    // Only paste if it's all digits and correct length
    if (/^\d+$/.test(pastedData) && pastedData.length === length) {
      onChange(pastedData);
      // Focus last input
      inputRefs.current[length - 1]?.focus();
      // Call onComplete after paste
      if (onComplete) {
        onComplete(pastedData);
      }
    }
  };

  return (
    <VerificationCodeInputContainer>
      {Array.from({ length }).map((_, index) => (
        <VerificationCodeInput
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(index, e.target.value)}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          autoComplete="one-time-code"
        />
      ))}
    </VerificationCodeInputContainer>
  );
}
